"use client";

import React, { useState } from "react";
import toast from "react-hot-toast";
import { Box, Typography } from "@mui/material";
import { Plus } from "lucide-react";
import { Button } from "@/ui";
import {
  RightColumn,
  SectionHeader,
  SectionContent,
} from "./styles/SharedStyles";
import ExperienceCard from "@components/shared/ExperienceCard";
import ReorderRoles from "@components/shared/modals/ReorderRoles";
import EditRoles from "../modals/EditRoles";
import {
  useRemoveCandidateRole,
  useUpdateCandidateRole,
  useAddCandidateRole,
  useReorderCandidateRoles,
} from "@lib/candidates/useRoleMutations";
import { ProfileSkeleton } from "./skeletons";

const Roles = ({ candidate, isImporting }) => {
  const [editingRole, setEditingRole] = useState(null);
  const [reorderOpen, setReorderOpen] = useState(false);
  const addRole = useAddCandidateRole();
  const updateRole = useUpdateCandidateRole();
  const removeRole = useRemoveCandidateRole();
  const reorderRoles = useReorderCandidateRoles();

  const roles = candidate.roles ?? [];

  const handleEditRole = (role) => {
    setEditingRole(role);
  };

  const handleUpdateRole = (updatedRole) => {
    const toastId = toast.loading("Saving...");
    updateRole.mutate(
      { candidate, role: updatedRole },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  const handleAddRole = () => {
    const toastId = toast.loading("Adding...");
    addRole.mutate(
      {
        candidate,
        role: { name: "New role", role: "", time_period: "", description: "" },
      },
      {
        onSuccess: (data) => {
          toast.dismiss(toastId);
          setEditingRole(data.data.role);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to add role");
        },
      }
    );
  };

  // ReorderRoles expects the {id, name, position, logo} shape it shares with
  // the Float creation flow — map each candidate role into that view before
  // handing it over, and translate the reordered result back to just an id
  // order for the mutation.
  const reorderItems = roles.map((role) => ({
    id: role.id,
    name: role.name,
    position: role.role,
    logo: role.image || role.company_logo,
  }));

  const handleReorderSave = (reorderedItems) => {
    const toastId = toast.loading("Saving changes...");
    reorderRoles.mutate(
      {
        candidate,
        order: reorderedItems.map((item) => item.id),
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to reorder roles");
        },
      }
    );
  };

  const handleDeleteRole = (roleId) => {
    const toastId = toast.loading("Removing role...");
    removeRole.mutate(
      { candidate, roleId },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to remove role");
        },
      }
    );
  };

  return (
    <Box id="candidate-section-roles">
      <SectionContent>
        <Box>
          <RightColumn>
            <SectionHeader>
              <Typography variant="h2">Recent roles</Typography>
            </SectionHeader>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: 2 }}>
              {roles.map((role) => (
                <ExperienceCard
                  key={role.id}
                  selected
                  hideCheckbox
                  logoSrc={role.image || role.company_logo}
                  title={role.name}
                  subtitle={role.role}
                  dateRange={role.time_period}
                  summary={role.description}
                  onReorderClick={roles.length > 1 ? () => setReorderOpen(true) : undefined}
                  onEdit={() => handleEditRole(role)}
                />
              ))}
              {isImporting && (
                <Box>
                  <ProfileSkeleton />
                </Box>
              )}
              <Button
                variant="tertiary"
                size="sm"
                noPadding
                startIcon={<Plus size={16} />}
                onClick={handleAddRole}
                style={{ alignSelf: "flex-start" }}
              >
                Add role
              </Button>
            </Box>
          </RightColumn>
        </Box>
      </SectionContent>
      <EditRoles
        key={editingRole?.id}
        open={editingRole !== null}
        onClose={() => setEditingRole(null)}
        role={editingRole}
        onUpdate={handleUpdateRole}
        onDelete={handleDeleteRole}
        candidate={candidate}
      />
      <ReorderRoles
        open={reorderOpen}
        onClose={() => setReorderOpen(false)}
        roles={reorderItems}
        onSave={handleReorderSave}
        description="Drag roles up or down to reorder them — the new order will be reflected on the candidate's profile."
      />
    </Box>
  );
};

export default Roles;
